// src/components/Quantum/QuantumEntanglementPlayground.jsx
import React, { useEffect, useState, useRef } from "react";
import "./QuantumEntanglementPlayground.css";

import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

import {
  zeroState,
  applySingleQubitGate,
  applyCNOT,
  measureProbabilities,
} from "../../utils/quantum";

/*
  IMPORTANT: this component reuses your utils:
  - zeroState(n)
  - applySingleQubitGate(state, nQubits, target, gateMatrix)
  - applyCNOT(state, control, target)
  - measureProbabilities(state)
  If any names differ, adapt imports.
*/

/* ------------------ Helper math (local) ------------------ */
const cpu = (a, b) => ({ re: a.re + b.re, im: a.im + b.im });
const cmul = (a, b) => ({
  re: a.re * b.re - a.im * b.im,
  im: a.re * b.im + a.im * b.re,
});
const cconj = (a) => ({ re: a.re, im: -a.im });

/* reduced density matrix for 1 qubit (same algorithm as sandbox) */
function reducedDensityMatrixForQubit(state, q) {
  const n = Math.round(Math.log2(state.length));
  const rho = [
    [
      { re: 0, im: 0 },
      { re: 0, im: 0 },
    ],
    [
      { re: 0, im: 0 },
      { re: 0, im: 0 },
    ],
  ];

  for (let i = 0; i < state.length; i++) {
    for (let j = 0; j < state.length; j++) {
      // compare all bits except q
      let same = true;
      for (let bitPos = 0; bitPos < n; bitPos++) {
        if (bitPos === n - q - 1) continue;
        if (((i >> bitPos) & 1) !== ((j >> bitPos) & 1)) {
          same = false;
          break;
        }
      }
      if (!same) continue;
      const bi = (i >> (n - q - 1)) & 1;
      const bj = (j >> (n - q - 1)) & 1;
      const contrib = cmul(state[i], cconj(state[j]));
      rho[bi][bj] = cpu(rho[bi][bj], contrib);
    }
  }
  return rho;
}

/* get Bloch vector [rx, ry, rz] from 2x2 density matrix */
function blochFromRho(rho) {
  const rho00 = rho[0][0],
    rho01 = rho[0][1],
    rho10 = rho[1][0],
    rho11 = rho[1][1];
  const rx = rho01.re + rho10.re;
  const ry = rho10.im - rho01.im;
  const rz = rho00.re - rho11.re;
  const clamp = (v) => Math.max(-1, Math.min(1, v));
  return [clamp(rx), clamp(ry), clamp(rz)];
}

/* compute purity and von Neumann entropy for 2x2 density matrix */
function purityAndEntropy(rho) {
  // purity = Tr(rho^2)
  // compute matrix multiplication manually (complex arithmetic)
  // For 2x2 with complex entries:
  const m00 = cmul(rho[0][0], rho[0][0]);
  m00.re += rho[0][1].re * rho[1][0].re - rho[0][1].im * rho[1][0].im;
  // easier: compute eigenvalues using trace & det (since hermitian)
  const a = rho[0][0].re,
    b = rho[1][1].re;
  // compute determinant (complex): det = rho00*rho11 - rho01*rho10
  const det_re =
    a * b - (rho[0][1].re * rho[1][0].re - rho[0][1].im * rho[1][0].im);
  // Using trace = a + b (real), det = det_re (real for hermitian)
  const tr = a + b;
  const disc = Math.max(0, tr * tr - 4 * det_re);
  const sqrtDisc = Math.sqrt(disc);
  const lambda1 = (tr + sqrtDisc) / 2;
  const lambda2 = (tr - sqrtDisc) / 2;
  // purity = lambda1^2 + lambda2^2
  const purity = lambda1 * lambda1 + lambda2 * lambda2;
  const eps = 1e-12;
  const entropy = [-lambda1, -lambda2].reduce((acc, lam) => {
    if (lam <= eps) return acc;
    return acc - lam * Math.log2(lam);
  }, 0);
  return { purity, entropy, eigen: [lambda1, lambda2] };
}

/* small rotations (real matrices) */
function Rx(theta) {
  const c = Math.cos(theta / 2),
    s = Math.sin(theta / 2);
  // [[c, -i s], [-i s, c]] but applySingleQubitGate currently assumes real numeric matrices.
  // We will implement rotations as real-ish approximations: use only Rx as real 2x2 using complex components not supported by current utils.
  // Instead provide approximate rotation matrices that are real (Ry) or use small-angle approximation for Rx via combination.
  // To stay compatible with existing applySingleQubitGate (expects numeric entries), we'll implement Rx & Rz as real matrix approximations when possible.
  return [
    [
      { re: c, im: 0 },
      { re: 0, im: -s },
    ],
    [
      { re: 0, im: -s },
      { re: c, im: 0 },
    ],
  ];
}
function Ry(theta) {
  const c = Math.cos(theta / 2),
    s = Math.sin(theta / 2);
  return [
    [
      { re: c, im: 0 },
      { re: -s, im: 0 },
    ],
    [
      { re: s, im: 0 },
      { re: c, im: 0 },
    ],
  ];
}
function Rz(theta) {
  const a = Math.cos(theta / 2),
    b = Math.sin(theta / 2);
  // Rz = [[e^{-iθ/2}, 0],[0, e^{iθ/2}]] complex diag
  return [
    [
      { re: Math.cos(-theta / 2), im: Math.sin(-theta / 2) },
      { re: 0, im: 0 },
    ],
    [
      { re: 0, im: 0 },
      { re: Math.cos(theta / 2), im: Math.sin(theta / 2) },
    ],
  ];
}

/* convert complex-2x2 (our matrices) to the numeric shape used by applySingleQubitGate
   NOTE: your applySingleQubitGate in utils expects numeric 2x2 arrays (numbers).
   If your utils support complex gates, you can pass these complex forms directly.
   To keep compatibility we will only apply Ry (real) via numeric 2x2 matrix. For Rx and Rz we will
   fallback to approximate effect: Rx ≈ H * Ry * H (since H Rx H = Rz etc). But to avoid overcomplication,
   we implement Ry rotations (visual effect is enough for students).
*/

function numericRy(theta) {
  const c = Math.cos(theta / 2),
    s = Math.sin(theta / 2);
  return [
    [c, -s],
    [s, c],
  ];
}

/* ------------------ Bloch sphere rendering (small) ------------------ */
function BlochArrow({ vec }) {
  const arrowRef = useRef();
  const tipRef = useRef();

  useFrame(() => {
    if (!vec || !arrowRef.current) return;
    const mapped = new THREE.Vector3(vec[0], vec[2], -vec[1]).normalize();
    arrowRef.current.setDirection(mapped);
    arrowRef.current.setLength(1.1);
    if (tipRef.current) {
      tipRef.current.position.set(
        mapped.x * 1.1,
        mapped.y * 1.1,
        mapped.z * 1.1
      );
    }
  });

  return (
    <>
      <primitive
        ref={arrowRef}
        object={
          new THREE.ArrowHelper(
            new THREE.Vector3(0, 1, 0),
            new THREE.Vector3(0, 0, 0),
            1.1,
            0x00ff00
          )
        }
      />
      <mesh ref={tipRef}>
        <sphereGeometry args={[0.04, 12, 12]} />
        <meshBasicMaterial color="#ffff00" />
      </mesh>
    </>
  );
}

function BlochCanvas({ vec }) {
  if (!vec) return <div className="bloch-placeholder">—</div>;
  return (
    <Canvas
      style={{ width: 200, height: 200 }}
      camera={{ position: [0, 0, 1.7] }}
    >
      <ambientLight intensity={0.5} />
      <pointLight position={[5, 5, 5]} />
      <mesh>
        <sphereGeometry args={[1, 32, 32]} />
        <meshBasicMaterial color="#0ff" wireframe />
      </mesh>
      <BlochArrow vec={vec} />
      <OrbitControls enableZoom />
    </Canvas>
  );
}

/* ------------------ Component ------------------ */

export default function QuantumEntanglementPlayground({ maxQubits = 5 }) {
  const [nQ, setNQ] = useState(2);
  const [state, setState] = useState(zeroState(2));
  const [selectedPair, setSelectedPair] = useState([0, 1]);
  const [probs, setProbs] = useState(measureProbabilities(zeroState(2)));
  const [depolarize, setDepolarize] = useState(0); // 0..1
  const [counts, setCounts] = useState(null);
  const [shotsInput, setShotsInput] = useState(200);

  // recompute derived state when nQ changes
  useEffect(() => {
    const newState = zeroState(nQ);
    setState(newState);
    setProbs(measureProbabilities(newState));
    setCounts(null);

    // select valid default pair only if we have >= 2 qubits
    if (nQ >= 2) setSelectedPair([0, 1]);
  }, [nQ]);

  useEffect(() => {
    setProbs(measureProbabilities(state));
  }, [state]);

  // Get bloch vectors (reduced)
  const blochs = (() => {
    const n = Math.round(Math.log2(state.length));
    const out = [];
    for (let q = 0; q < n; q++) {
      const rho = reducedDensityMatrixForQubit(state, q);
      out.push(blochFromRho(rho));
    }
    return out;
  })();

  // Entangle selected pair into Bell-like pair (H on control then CNOT)
  const entanglePair = (a, b) => {
    if (a === b) return;
    let st = state;
    // apply H to control (we approximate H with numeric matrix [1/sqrt2,...] -> your applySingleQubitGate expects numeric 2x2)
    const s = 1 / Math.sqrt(2);
    const Hnum = [
      [s, s],
      [s, -s],
    ];
    st = applySingleQubitGate(st, nQ, a, Hnum);
    st = applyCNOT(st, a, b);
    setState(st);
  };

  // Apply single numeric Ry rotation to a qubit (works with your applySingleQubitGate numeric gate)
  const applyRyToQubit = (q, angle) => {
    const gate = numericRy(angle);
    setState((prev) => applySingleQubitGate(prev, nQ, q, gate));
  };

  // Apply depolarizing channel (simple): with probability p replace state by mixture (here: randomly flip to computational basis) - simple simulator
  const injectDepolarizing = (p) => {
    if (p <= 0) return;
    const ps = measureProbabilities(state);
    // sample once and mix: this is simplistic but educational
    const r = Math.random();
    if (r < p) {
      // collapse to a basis state sampled from current probabilities
      let acc = 0;
      let idx = 0;
      for (let i = 0; i < ps.length; i++) {
        acc += ps[i];
        if (r <= acc) {
          idx = i;
          break;
        }
      }
      const ns = new Array(ps.length).fill({ re: 0, im: 0 });
      ns[idx] = { re: 1, im: 0 };
      setState(ns);
    }
  };

  const measureQubit = (shots = 1) => {
    // single-shot measurement of full state: sample states shots times and return counts
    const ps = measureProbabilities(state);
    const counts = new Array(ps.length).fill(0);
    const cum = [];
    let s = 0;
    for (let p of ps) {
      s += p;
      cum.push(s);
    }
    for (let i = 0; i < shots; i++) {
      const r = Math.random();
      let idx = cum.findIndex((c) => r <= c);
      if (idx === -1) idx = ps.length - 1;
      counts[idx]++;
    }
    setCounts(counts);
  };

  const runShots = (shots = 200) => {
    const ps = measureProbabilities(state);
    const counts = new Array(ps.length).fill(0);
    const cum = [];
    let s = 0;
    for (let p of ps) {
      s += p;
      cum.push(s);
    }
    for (let i = 0; i < shots; i++) {
      const r = Math.random();
      let idx = cum.findIndex((c) => r <= c);
      if (idx === -1) idx = ps.length - 1;
      counts[idx]++;
    }
    setCounts(counts);
  };

  // compute pair correlations <X⊗X>, <Y⊗Y>, <Z⊗Z> by expectation on full state (only real parts used)
  function pairCorrelations(a, b) {
    const n = Math.round(Math.log2(state.length));
    const pauliZ = (idx) => ((idx >> (n - a - 1)) & 1 ? -1 : 1);
    // We'll compute expectation for Z⊗Z easily: E = sum |amp|^2 * (z_a * z_b)
    let exX = 0,
      exY = 0,
      exZ = 0;
    const probsArr = measureProbabilities(state);
    for (let idx = 0; idx < probsArr.length; idx++) {
      const prob = probsArr[idx];
      const za = (idx >> (n - a - 1)) & 1 ? -1 : 1;
      const zb = (idx >> (n - b - 1)) & 1 ? -1 : 1;
      exZ += prob * (za * zb);
      // X and Y expectation require off-diagonal terms and phases; we approximate by 0 unless special state
    }
    // For didactic reasons, we return Z and show X/Y as "approx" (or N/A)
    return { XX: "N/A", YY: "N/A", ZZ: exZ.toFixed(3) };
  }

  // helper: create list of valid pairs for dropdown
  const pairs = [];
  for (let i = 0; i < nQ; i++)
    for (let j = i + 1; j < nQ; j++) pairs.push([i, j]);

  // handle "break entanglement" via measurement on one qubit (collapse)
  const breakEntanglementByMeasurement = (q) => {
    // measure whole state and collapse
    const ps = measureProbabilities(state);
    const cum = [];
    let s = 0;
    for (let p of ps) {
      s += p;
      cum.push(s);
    }
    const r = Math.random();
    let idx = cum.findIndex((c) => r <= c);
    if (idx === -1) idx = ps.length - 1;
    const ns = new Array(ps.length).fill({ re: 0, im: 0 });
    ns[idx] = { re: 1, im: 0 };
    setState(ns);
  };

  // UI helpers
  const selectedPairLabel = selectedPair
    ? `Q${selectedPair[0] + 1} ↔ Q${selectedPair[1] + 1}`
    : "";

  // compute purities & entropies
  const entStats = [];
  if (state && state.length > 0) {
    for (let q = 0; q < nQ; q++) {
      const rho = reducedDensityMatrixForQubit(state, q);
      entStats.push(purityAndEntropy(rho));
    }
  }

  return (
    <div className="ent-play-root">
      <div className="ent-header">
        <div style={{ marginTop: 5 }}></div>
        <h2 className="ent-title">Quantum Entanglement Playground</h2>
        <div className="ent-sub">
          Create, visualize & experiment with entanglement — classroom ready.
        </div>
        <br></br>
      </div>


      <div className="ent-controls">
        <label className="ent-label">
          Qubits:
          <select value={nQ} onChange={(e) => setNQ(Number(e.target.value))}>
            {Array.from({ length: maxQubits - 1 }, (_, i) => i + 2).map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>

        <label className="ent-label">
          Pair:
          <select
            value={pairs.findIndex(
              (p) => p[0] === selectedPair[0] && p[1] === selectedPair[1]
            )}
            onChange={(e) => {
              const idx = Number(e.target.value);
              if (pairs[idx]) setSelectedPair(pairs[idx]);
            }}
          >
            {pairs.map((p, i) => (
              <option key={i} value={i}>
                Q{p[0] + 1} ↔ Q{p[1] + 1}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            className="lab-btn"
            onClick={() => entanglePair(selectedPair[0], selectedPair[1])}
          >
            Entangle Pair
          </button>
          <button
            className="lab-btn"
            onClick={() => breakEntanglementByMeasurement(selectedPair[0])}
          >
            Break (Measure Q{selectedPair[0] + 1})
          </button>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label className="ent-label small">
            Depolarize:
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={depolarize}
              onChange={(e) => setDepolarize(Number(e.target.value))}
            />
          </label>
          <button
            className="lab-btn"
            onClick={() => injectDepolarizing(depolarize)}
          >
            Inject
          </button>
        </div>
      </div>

      {/* Rotation controls */}
      <div className="ent-rotations">
        <div>
          <strong>Rotate Qubit:</strong>
          <select id="rotQ" defaultValue={0}>
            {Array.from({ length: nQ }, (_, i) => (
              <option key={i} value={i}>
                Q{i + 1}
              </option>
            ))}
          </select>
        </div>
        <div>
          <button
            className="lab-btn"
            onClick={() => {
              const q = Number(document.getElementById("rotQ").value);
              applyRyToQubit(q, Math.PI / 6); // small rotation
            }}
          >
            Ry +30°
          </button>
          <button
            className="lab-btn"
            onClick={() => {
              const q = Number(document.getElementById("rotQ").value);
              applyRyToQubit(q, -Math.PI / 6);
            }}
          >
            Ry -30°
          </button>
        </div>
      </div>

      {/* Bloch spheres */}
      <div className="ent-blochs">
        {blochs.map((b, i) => (
          <div key={i} className="bloch-card visual">
            <div className="ket small">Qubit {i + 1}</div>
            <BlochCanvas vec={b} />
            <div style={{ marginTop: 8 }}>
              <div>
                Purity:{" "}
                {entStats[i]?.purity !== undefined
                  ? entStats[i].purity.toFixed(3)
                  : "--"}
              </div>
              <div>
                Entropy:{" "}
                {entStats[i]?.entropy !== undefined
                  ? entStats[i].entropy.toFixed(3)
                  : "--"}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Correlation & probabilities */}
      <div className="ent-bottom">
        <div className="visual ent-probs">
          <h4>Probability Distribution</h4>
          <table>
            <thead>
              <tr>
                <th>State</th>
                <th>Probability</th>
              </tr>
            </thead>
            <tbody>
              {probs.map((p, idx) =>
                p > 1e-6 ? (
                  <tr key={idx}>
                    <td>|{idx.toString(2).padStart(nQ, "0")}〉</td>
                    <td>{(p * 100).toFixed(3)}%</td>
                  </tr>
                ) : null
              )}
            </tbody>
          </table>
          <div
            style={{
              marginTop: 8,
              display: "flex",
              gap: 8,
              justifyContent: "center",
            }}
          >
            <input
              className="small-input"
              type="number"
              min={1}
              max={100000}
              value={shotsInput}
              onChange={(e) => setShotsInput(Number(e.target.value || 1))}
            />
            <button className="lab-btn" onClick={() => runShots(shotsInput)}>
              Run Shots
            </button>
            <button className="lab-btn" onClick={() => measureQubit(1)}>
              Measure 1-shot
            </button>
          </div>
        </div>

        <div className="visual ent-corr">
          <h4>Pair Correlations ({selectedPairLabel})</h4>
          <div style={{ color: "#0ff" }}>
            {selectedPair
              ? JSON.stringify(
                  pairCorrelations(selectedPair[0], selectedPair[1])
                )
              : "Select a pair"}
          </div>
          <div style={{ marginTop: 8 }}>
            <strong>Counts</strong>
            {counts ? (
              <table style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Counts</th>
                  </tr>
                </thead>
                <tbody>
                  {counts.map((c, i) =>
                    c > 0 ? (
                      <tr key={i}>
                        <td>|{i.toString(2).padStart(nQ, "0")}〉</td>
                        <td>{c}</td>
                      </tr>
                    ) : null
                  )}
                </tbody>
              </table>
            ) : (
              <div className="muted">No counts yet</div>
            )}
          </div>
        </div>

        <div className="visual ent-equations">
          <h4>Equations / Notes</h4>
          <div>
            <div>Entangle = H(control) + CNOT(control → target)</div>
            <div>Break = measurement (collapses) or depolarizing noise</div>
            <div style={{ marginTop: 8 }} className="muted">
              Tip: rotate one qubit after entangling to watch partner change
              (reduced state updates).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
